import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "contexts/ToastContext";
import { useTranslation } from "react-i18next";

import { apiQuery } from "utils/queries";
import { favoriteContentQuery, libraryObjectQuery } from "./content";
import { api } from "./restUtil";
import { isActive, isSuspended } from "./users";
import { isUserTeacher } from "./users/utils";
import { StudentStatus, StudentStatusEnum } from "./student/model";

type FavoriteStudentsQuery = {
  student_status?: string;
};

type RawStudentsFavorite = {
  id: string;
  status: string;
  email: string;
  first_name: string;
  last_name: string;
  city: string | null;
  country: string | null;
  timezone: { id: string; utc_offset: string };
  last_seen: string | null;
  number_of_lessons?: number;
  student: { status: StudentStatus };
  primary_instructor: { id: string; first_name: string; last_name: string } | null;
};

type RawStudentsFavoritesResponse = {
  list: RawStudentsFavorite[];
};

export type FavoriteStudent = {
  id: string;
  status: string;
  // Last activity in the app. The roster uses it to group dormant students.
  last_seen: string | null;
  profile: {
    email: string;
    first_name: string;
    last_name: string;
    timezone: { id: string; utc_offset: string };
    address: { city: string | null; country: string | null };
  };
  student: {
    number_of_lessons: number | undefined;
    status: StudentStatus;
  };
  primary_instructor: { id: string; first_name: string; last_name: string } | null;
};

type FavoriteStudentListResponse = {
  list: FavoriteStudent[];
};

/**
 * Every student assigned to the signed-in instructor, in every status.
 *
 * The roster asks for all statuses on purpose: finished students are not rendered
 * inline, but the count and the disclosure that reveals them need those records.
 * Grouping happens on the client in `studentGroups`.
 */
export const favoriteStudentsQuery = apiQuery<FavoriteStudentListResponse>({
  key: "/students/favorites",
  queryFn: () =>
    getFavoritesForInstructor({
      student_status: Object.values(StudentStatusEnum).join(","),
    }),
  // A teacher's roster changes on the order of days, and this returns every student
  // they have ever been assigned. The panel invalidates the key itself when it removes
  // someone, so nothing here depends on polling.
  staleTime: 10 * 60 * 1000,
  refetchOnWindowFocus: false,
});

/**
 * A teacher's students in the two shapes assigning work needs them: `assignable` is
 * the ones a lesson can be handed to straight away — their own, still taking lessons
 * — while `roster` is everyone ever assigned to them, in any status, for choosing
 * from the whole list. Both are empty for anyone who is not a teacher.
 */
export function useAssignableStudents(instructorId: string) {
  const roster = useQuery({
    ...favoriteStudentsQuery(),
    enabled: isUserTeacher(),
  }).data?.list;

  const assignable = useMemo(
    () =>
      (roster ?? []).filter(student => {
        const isActiveUser = isActive(student.status) || isSuspended(student.status);
        const isStudying =
          student.student.status !== "graduated" &&
          student.student.status !== "pilot-completed";
        return (
          isActiveUser && isStudying && student.primary_instructor?.id === instructorId
        );
      }),
    [roster, instructorId]
  );

  return { roster: roster ?? [], assignable };
}

// When called with undefined, applies a default student_status filter (all except graduated).
// When called with null returns all statuses.
export function getFavoritesForInstructor(
  query?: FavoriteStudentsQuery | null
): Promise<FavoriteStudentListResponse> {
  const params: Record<string, string> = { sort: "name" };
  if (query === undefined) {
    params.student_status = Object.values(StudentStatusEnum)
      .filter(status => status !== StudentStatusEnum.GRADUATED)
      .join(",");
  } else if (query !== null) {
    Object.assign(params, query);
  }
  return api
    .get<RawStudentsFavoritesResponse>("/students/favorites", params)
    .then(resp => ({
      list: resp.list.map(s => ({
        id: s.id,
        status: s.status,
        last_seen: s.last_seen,
        profile: {
          email: s.email,
          first_name: s.first_name,
          last_name: s.last_name,
          timezone: { id: s.timezone.id, utc_offset: s.timezone.utc_offset },
          address: { city: s.city, country: s.country },
        },
        student: {
          number_of_lessons: s.number_of_lessons,
          status: s.student.status,
        },
        primary_instructor: s.primary_instructor,
      })),
    }));
}

export function removeStudentFromFavorites(studentId: string): Promise<void> {
  return api.delete<{ id: string }>("/students/favorites", { id: studentId });
}

function addFavouriteCourse(id: string): Promise<void> {
  return api.post(`/library/${id}/favorite`);
}

function removeFavouriteCourse(id: string): Promise<void> {
  return api.delete(`/library/${id}/favorite`);
}

/**
 * Whether a library object is bookmarked, and the toggle for it. The bookmark answers
 * at once and goes back — under a toast — if the request fails.
 *
 * `is_favorite` comes from whichever read the caller already holds the object through.
 * Only the object's own query is refreshed after a write — the lists it also appears
 * in are not, so a card in one of those keeps the answer it was given until the list
 * is read again.
 */
export function useToggleFavourite(object: {
  id?: string;
  is_favorite?: boolean | null;
}) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { t } = useTranslation();

  const update = useMutation({
    mutationFn: ({ id, next }: { id: string; next: boolean }) =>
      next ? addFavouriteCourse(id) : removeFavouriteCourse(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: libraryObjectQuery.baseKey });
      // The bookmarked list is the flag, so it cannot wait for its stale time.
      void queryClient.invalidateQueries({
        queryKey: favoriteContentQuery.baseKey,
      });
    },
    onError: () => showToast(t("Learn.bookmark.updateFailed"), "error"),
  });

  const isFavourite =
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- TanStack types variables as set once the mutation is past idle; it is not
    update.isPending || update.isSuccess ? update.variables?.next : !!object.is_favorite;

  return {
    isFavourite,
    toggle: () => {
      // Cards are rendered from partial objects too, which carry no id.
      if (object.id) {
        update.mutate({ id: object.id, next: !isFavourite });
      }
    },
  };
}

// Admin favorites — the /admin/{role}/favorites pair, where the role in the path
// picks which side of the pairing the caller is administering. Both variants take
// and return the same shapes.
export type AdminFavoriteRole = "student" | "instructor";

export type AdminFavorite = {
  id: string;
  first_name: string;
  last_name: string | null;
};

type AdminFavoritesArgs = {
  role: AdminFavoriteRole;
  user_id: string;
};

type AdminFavoritesResponse = {
  list: AdminFavorite[];
  // Only the instructor variant reports it.
  total?: number;
};

async function getAdminFavorites(args: AdminFavoritesArgs) {
  const resp = await api.get<AdminFavoritesResponse>(adminFavoritesPath(args.role), {
    [`${args.role}_id`]: args.user_id,
  });
  return resp.list;
}

// The path varies with the role, so the key is a fixed label rather than a route.
export const adminFavoritesQuery = apiQuery<AdminFavorite[], AdminFavoritesArgs>({
  key: "admin-favorites",
  queryFn: ({ params }) => getAdminFavorites(params),
});

// The association is symmetric, so both endpoints name both sides whichever role
// owns the path.
type AdminFavoriteArgs = {
  role: AdminFavoriteRole;
  student_id: string;
  instructor_id: string;
};

type AdminFavoritePayload = Omit<AdminFavoriteArgs, "role">;

export function addAdminFavorite({ role, ...payload }: AdminFavoriteArgs) {
  return api.post<AdminFavoritePayload>(adminFavoritesPath(role), payload);
}

export function addStudentFavorite(payload: AdminFavoritePayload) {
  return addAdminFavorite({ role: "student", ...payload });
}

// Removal identifies the pair by query string, not by body.
export function removeAdminFavorite({ role, ...query }: AdminFavoriteArgs) {
  return api.delete<void, void, AdminFavoritePayload>(
    adminFavoritesPath(role),
    undefined,
    { query }
  );
}

function adminFavoritesPath(role: AdminFavoriteRole) {
  return `/admin/${role}/favorites`;
}
